import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import LanguageSwitch from '../components/LanguageSwitch';
import {
  clearQualificationResponses,
  initQualificationResponses,
  setQualificationOutcome,
  upsertQualificationAnswer,
} from '../utils/qualificationResponses';
import LegalModal from '../components/LegalModal';
import AppHeader from '../components/AppHeader';
import './Qualify.css';

const STEP_MAP = {
  q1: {
    textKey: 'qualify.q1',
    onYes: 'q2',
    onNo: 'alternatives',
  },
  q2: {
    textKey: 'qualify.q2',
    onYes: 'q3',
    onNo: 'q5',
  },
  q3: {
    textKey: 'qualify.q3',
    onYes: 'alternatives',
    onNo: 'q4',
  },
  q4: {
    textKey: 'qualify.q4',
    onYes: 'alternatives',
    onNo: 'q5',
  },
  q5: {
    textKey: 'qualify.q5',
    onYes: 'alternatives',
    onNo: 'qualified',
  },
};

export default function Qualify() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState('q1');
  const [history, setHistory] = useState([]);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const question = STEP_MAP[step];
  const isAlternatives = step === 'alternatives';

  useEffect(() => {
    initQualificationResponses();
  }, []);

  const goTo = (next, answer = null) => {
    if (question && answer) {
      upsertQualificationAnswer({
        questionId: step,
        questionText: t(question.textKey),
        answer,
      });
    }

    if (next === 'qualified') {
      setQualificationOutcome('qualified');
      navigate('/es/regularizacion/qualify/qualified');
      return;
    }

    if (next === 'alternatives') {
      setQualificationOutcome('alternatives');
    }

    setHistory((prev) => [...prev, step]);
    setStep(next);
  };

  const startOver = () => {
    clearQualificationResponses();
    initQualificationResponses();
    setStep('q1');
    setHistory([]);
  };

  return (
    <div className="qualify-page">
      <AppHeader onBrandClick={() => navigate('/')} rightSlot={<LanguageSwitch />} />

      <main className="qualify-main">
        <div className="qualify-row">
          <span className="qualify-eyebrow">{t('qualify.eyebrow')}</span>
          {!isAlternatives && <span className="qualify-badge">{t('qualify.badge')}</span>}
        </div>

        {step === 'q1' && (
          <>
            <h1 className="qualify-title">
              <Trans i18nKey="qualify.title" components={{ accent: <span /> }} />
            </h1>
            <p className="qualify-sub">{t('qualify.subtitle')}</p>
          </>
        )}

        <section className="qualify-card">
          {!isAlternatives && question ? (
            <>
              <h2 className="qualify-question">{t(question.textKey)}</h2>
              <div className="qualify-actions">
                <button type="button" className="qualify-option" onClick={() => goTo(question.onYes, 'yes')}>
                  <span className="qualify-option-left">
                    <span className="qualify-option-icon">✓</span>
                    {t('qualify.yes')}
                  </span>
                  <span className="qualify-arrow">→</span>
                </button>
                <button type="button" className="qualify-option" onClick={() => goTo(question.onNo, 'no')}>
                  <span className="qualify-option-left">
                    <span className="qualify-option-icon">×</span>
                    {t('qualify.no')}
                  </span>
                  <span className="qualify-arrow">→</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="qualify-question">
                <Trans i18nKey="qualify.altTitle" components={{ accent: <span /> }} />
              </h2>
              <p className="qualify-sub">{t('qualify.altDesc')}</p>
              <button type="button" className="qualify-continue" onClick={() => navigate('/es/regularizacion/registro')}>
                {t('qualify.continue')}
              </button>
            </>
          )}
        </section>

        {history.length > 0 && (
          <button type="button" className="qualify-start-over" onClick={startOver}>
            <i className="ri-refresh-line" aria-hidden />
            {t('qualify.startOver')}
          </button>
        )}

        {!isAlternatives && (
          <p className="qualify-privacy">
            <i className="ri-shield-check-line qualify-privacy-icon" aria-hidden />
            <span>
              <Trans
                i18nKey="qualify.qualifiedPrivacy"
                components={{
                  privacy: (
                    <button
                      type="button"
                      className="qualify-privacy-link"
                      onClick={() => setPrivacyOpen(true)}
                    />
                  ),
                }}
              />
            </span>
          </p>
        )}
      </main>

      <LegalModal type="privacy" open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}
